import React, { useState } from "react";
import {
  Flex,
  Typography,
  Card,
  Divider,
  Badge,
  Row,
  Col,
  message,
} from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  WarningOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useCallHistoryProvider } from "../context/CallHIstoryContext";
import { useTheme } from "../../../ThemeContext";
import { IconButton, Tooltip } from "@mui/material";
import ThemedChip from "../../common/ThemedChip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { downloadWhitesheet } from "../../../utils/apiClient";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";


const { Text, Title } = Typography;

const CallSummaryDisplay = ({ setRenderType }) => {
  const { currentTheme, themeMode } = useTheme();
  const { callContent } = useCallHistoryProvider();
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    callDetails: true,
    detailedBreakdown: true,
    memberDetails: true,
  });

  const { setSelectedMember } = useCallHistoryProvider();

  const isDark = themeMode === "dark";

  // Helper function to filter out NA values from data
  const filterNAValues = (data) => {
    if (!data) return null;
    
    if (Array.isArray(data)) {
      return data.map(item => filterNAValues(item)).filter(item => item !== null);
    }
    
    if (typeof data === "object") {
      const filtered = {};
      Object.entries(data).forEach(([key, value]) => {
        // Only include values that are not NA, null, undefined, or empty string
        if (value !== "NA" && value !== "N/A" && value !== null && value !== undefined && value !== "") {
          filtered[key] = value;
        }
      });
      
      // Return null if no valid data remains
      return Object.keys(filtered).length > 0 ? filtered : null;
    }
    
    // For primitive values, return null if it's an NA value
    if (data === "NA" || data === "N/A" || data === "" || data === null || data === undefined) {
      return null;
    }
    
    return data;
  };

  // Extract the new API response structure - data is now directly in callContent
  const callSummaryData = {
    call_details: filterNAValues(callContent?.call_details),
    detailed_breakdown: filterNAValues(callContent?.detailed_breakdown),
    member_details: filterNAValues(callContent?.member_details),
  };

  // Check if we have any data to display
  const hasData =
    callSummaryData.call_details ||
    callSummaryData.detailed_breakdown ||
    callSummaryData.member_details;

  // Helper function to safely convert any value to displayable string
  const safeValueToString = (value) => {
    if (value === null || value === undefined) {
      return "Not Available";
    }

    if (value === "NA" || value === "N/A" || value === "") {
      return "Not Available";
    }

    if (typeof value === "string") {
      return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return "Not Available";
      return value.map((item) => safeValueToString(item)).join(", ");
    }

    if (typeof value === "object") {
      try {
        const entries = Object.entries(value);
        if (entries.length === 0) return "Not Available";
        if (entries.length <= 3) {
          return entries
            .map(([k, v]) => `${k}: ${safeValueToString(v)}`)
            .join(", ");
        }
        return `Object with ${entries.length} properties`;
      } catch (e) {
        return "Complex data";
      }
    }

    return String(value);
  };

  // Helper function to format field names
  const formatFieldName = (key) => {
    if (!key || typeof key !== "string") return "Unknown Field";
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper function to get status color and icon
  const getStatusStyling = (key, value) => {
    const stringValue = safeValueToString(value);
    const isNA = stringValue === "Not Available";
    const isActive = stringValue !== "Not Available" && stringValue !== "NA";

    // Special handling for call status types
    const statusTypes = {
      plan_termination: {
        color: "#ff4d4f",
        icon: <ExclamationCircleOutlined />,
        severity: "high",
      },
      member_not_found: {
        color: "#faad14",
        icon: <WarningOutlined />,
        severity: "medium",
      },
      system_issue: {
        color: "#ff7a45",
        icon: <ExclamationCircleOutlined />,
        severity: "high",
      },
      restricted_plan: {
        color: "#ff4d4f",
        icon: <WarningOutlined />,
        severity: "high",
      },
      outoff_office: {
        color: "#faad14",
        icon: <ClockCircleOutlined />,
        severity: "medium",
      },
      robocalls: {
        color: "#ff4d4f",
        icon: <PhoneOutlined />,
        severity: "high",
      },
      call_queue_disconnection: {
        color: "#faad14",
        icon: <PhoneOutlined />,
        severity: "medium",
      },
      representative_holdtimeout: {
        color: "#faad14",
        icon: <ClockCircleOutlined />,
        severity: "medium",
      },
      ivr_disconnection: {
        color: "#faad14",
        icon: <PhoneOutlined />,
        severity: "medium",
      },
      outoff_network: {
        color: "#ff7a45",
        icon: <ExclamationCircleOutlined />,
        severity: "high",
      },
      call_status: {
        color: "#52c41a",
        icon: <CheckCircleOutlined />,
        severity: "low",
      },
    };

    const statusInfo = statusTypes[key] || {
      color: isDark ? "#fff" : "#333",
      icon: null,
      severity: "low",
    };

    return {
      color: isNA
        ? isDark
          ? "#666"
          : "#999"
        : isActive
        ? statusInfo.color
        : isDark
        ? "#fff"
        : "#333",
      backgroundColor:
        isActive && !isNA ? `${statusInfo.color}15` : "transparent",
      borderColor:
        isActive && !isNA
          ? `${statusInfo.color}30`
          : isDark
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.1)",
      icon: isActive && !isNA ? statusInfo.icon : null,
      severity: statusInfo.severity,
      isActive: isActive && !isNA,
    };
  };

  // Render individual field with enhanced styling
  const renderField = (key, value, isNested = false) => {
    const styling = getStatusStyling(key, value);
    const displayValue = safeValueToString(value);
    const isHighlighted = styling.isActive;

    return (
      <div
        key={key}
        style={{
          padding: "16px 20px",
          paddingLeft: isNested ? "32px" : "20px",
          backgroundColor: styling.backgroundColor,
          borderRadius: "8px",
          margin: "6px 0",
          border: `1px solid ${styling.borderColor}`,
          transition: "all 0.3s ease",
          boxShadow: isHighlighted ? `0 2px 8px ${styling.color}20` : "none",
        }}
      >
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={8} style={{ width: "60%" }}>
            {styling.icon && (
              <span style={{ color: styling.color, fontSize: "14px" }}>
                {styling.icon}
              </span>
            )}
            <Text
              style={{
                color: styling.color,
                fontSize: 15,
                fontWeight: isHighlighted ? "600" : "500",
                lineHeight: "1.4",
              }}
            >
              {formatFieldName(key)}
            </Text>
            {isHighlighted && styling.severity === "high" && (
              <Badge status="error" style={{ marginLeft: "4px" }} />
            )}
            {isHighlighted && styling.severity === "medium" && (
              <Badge status="warning" style={{ marginLeft: "4px" }} />
            )}
          </Flex>
          <div style={{ width: "40%", textAlign: "right", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            {isHighlighted ? (
              <ThemedChip
                variant={
                  styling.severity === "high"
                    ? "error"
                    : styling.severity === "medium"
                    ? "warning"
                    : "success"
                }
                size="small"
               
              >
                {displayValue}
              </ThemedChip>
            ) : (
              <Text
                style={{
                  color: styling.color,
                  fontSize: 14,
                  fontStyle:
                    displayValue === "Not Available" ? "italic" : "normal",
                  lineHeight: "1.4",
                }}
              >
                {displayValue}
              </Text>
            )}
          </div>
        </Flex>
      </div>
    );
  };

  // Render section with improved design
  const renderSection = (title, data, sectionKey, icon, description) => {
    if (!data) return null;

    console.log("dataDebugger", data);

    const isExpanded = expandedSections[sectionKey];
    const toggleSection = () => {
      setExpandedSections((prev) => ({
        ...prev,
        [sectionKey]: !prev[sectionKey],
      }));
    };

    // Count active/highlighted items (currently unused but kept for future features)
    // let activeCount = 0;
    // let totalCount = 0;

    // Counting logic commented out since activeCount and totalCount are not used
    /*
    if (Array.isArray(data)) {
      data.forEach((item) => {
        if (typeof item === "object") {
          Object.entries(item).forEach(([key, value]) => {
            totalCount++;
            const styling = getStatusStyling(key, value);
            if (styling.isActive) activeCount++;
          });
        } else {
          totalCount++;
          if (safeValueToString(item) !== "Not Available") activeCount++;
        }
      });
    } else if (typeof data === "object") {
      Object.entries(data).forEach(([key, value]) => {
        totalCount++;
        const styling = getStatusStyling(key, value);
        if (styling.isActive) activeCount++;
      });
    }
    */

    return (
      <Card
        style={{
          backgroundColor: isDark ? "#04002b" : "#ffffff",
          border: `1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)"
          }`,
          borderRadius: "12px",
          marginBottom: "20px",
          overflow: "hidden",
          boxShadow: isDark
            ? "0 4px 12px rgba(0, 0, 0, 0.3)"
            : "0 2px 8px rgba(0, 0, 0, 0.06)",
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* Section Header */}
        <div
          onClick={toggleSection}
          style={{
            padding: "20px 24px",
            backgroundColor: isDark ? "#242243" : "#fafafa",
            borderBottom: `1px solid ${
              isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)"
            }`,
            cursor: "pointer",
            transition: "background-color 0.2s ease",
          }}
        >
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={16}>
              <div
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  backgroundColor: `${currentTheme.loginPage.loginButtonColor}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {React.cloneElement(icon, {
                  style: {
                    color: currentTheme.loginPage.loginButtonColor,
                    fontSize: "18px",
                  },
                })}
              </div>
              <div>
                <Title
                  level={4}
                  style={{
                    margin: 0,
                    color: isDark ? "#fff" : "#333",
                    fontSize: 18,
                    marginBottom: "4px",
                  }}
                >
                  {title}
                </Title>
                {description && (
                  <Text
                    style={{
                      color: isDark ? "#ccc" : "#666",
                      fontSize: "12px",
                    }}
                  >
                    {description}
                  </Text>
                )}
              </div>
              {/* <Flex gap={8}>
                {activeCount > 0 && (
                  <ThemedChip
                    variant="error"
                    size="small"
                    customStyle={{ fontSize: "11px", borderRadius: "10px" }}
                  >
                    {activeCount} Active
                  </ThemedChip>
                )}
                <ThemedChip
                  variant="info"
                  size="small"
                  customStyle={{ fontSize: "11px", borderRadius: "10px" }}
                >
                  {totalCount} Total
                </ThemedChip>
              </Flex> */}
            </Flex>
            <div
              style={{
                padding: "4px 8px",
                borderRadius: "6px",
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.04)",
              }}
            >
              {isExpanded ? (
                <EyeInvisibleOutlined
                  style={{ color: isDark ? "#fff" : "#333", fontSize: "16px" }}
                />
              ) : (
                <EyeOutlined
                  style={{ color: isDark ? "#fff" : "#333", fontSize: "16px" }}
                />
              )}
            </div>
          </Flex>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div style={{ padding: "24px" }}>
            {Array.isArray(data) ? (
              // Handle array data (detailed_breakdown)
              data.map((item, index) => (
                <div
                  key={index}
                  style={{ marginBottom: index < data.length - 1 ? "24px" : 0 }}
                >
                  {data.length > 1 && (
                    <div
                      style={{
                        marginBottom: "16px",
                        padding: "8px 16px",
                        backgroundColor: isDark
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                        borderRadius: "6px",
                        borderLeft: `3px solid ${currentTheme.loginPage.loginButtonColor}`,
                      }}
                    >
                      <Text
                        style={{
                          color: isDark ? "#ccc" : "#666",
                          fontSize: "13px",
                          fontWeight: "600",
                        }}
                      >
                        Record {index + 1}
                      </Text>
                    </div>
                  )}
                  {typeof item === "object"
                    ? Object.entries(item).map(([key, value]) =>
                        renderField(key, value)
                      )
                    : renderField(`item_${index}`, item)}
                  {index < data.length - 1 && (
                    <Divider
                      style={{
                        margin: "20px 0",
                        borderColor: isDark
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.06)",
                      }}
                    />
                  )}
                </div>
              ))
            ) : (
              // Handle object data (call_details, member_details)
              <Row gutter={[16, 16]}>
                {Object.entries(data).map(([key, value], index) => (
                  <Col xs={24} sm={12} md={12} key={key}>
                    {renderField(key, value)}
                  </Col>
                ))}
              </Row>
            )}
          </div>
        )}
      </Card>
    );
  };

  // Copy to clipboard functionality
  const handleCopyToClipboard = () => {
    if (!callSummaryData) return;

    let textToCopy = "Call Summary Report\n" + "=".repeat(20) + "\n\n";

    // Add Member Details
    if (callSummaryData.member_details) {
      textToCopy += "MEMBER DETAILS:\n";
      Object.entries(callSummaryData.member_details).forEach(([key, value]) => {
        const displayValue = safeValueToString(value);
        textToCopy += `${formatFieldName(key)}: ${displayValue}\n`;
      });
      textToCopy += "\n";
    }

    // Add Call Details
    if (callSummaryData.call_details) {
      textToCopy += "CALL DETAILS:\n";
      Object.entries(callSummaryData.call_details).forEach(([key, value]) => {
        const displayValue = safeValueToString(value);
        textToCopy += `${formatFieldName(key)}: ${displayValue}\n`;
      });
      textToCopy += "\n";
    }

    // Add Detailed Breakdown
    if (callSummaryData.detailed_breakdown) {
      textToCopy += "DETAILED BREAKDOWN:\n";
      const breakdownData = Array.isArray(callSummaryData.detailed_breakdown)
        ? callSummaryData.detailed_breakdown
        : [callSummaryData.detailed_breakdown];

      breakdownData.forEach((item, index) => {
        if (breakdownData.length > 1) {
          textToCopy += `Record ${index + 1}:\n`;
        }
        if (item && typeof item === "object") {
          Object.entries(item).forEach(([key, value]) => {
            const displayValue = safeValueToString(value);
            textToCopy += `${formatFieldName(key)}: ${displayValue}\n`;
          });
        } else {
          textToCopy += `${safeValueToString(item)}\n`;
        }
        textToCopy += "\n";
      });
    }

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopyTooltipOpen(true);
        setTimeout(() => {
          setCopyTooltipOpen(false);
        }, 2000);
      })
      .catch((err) => console.error("Failed to copy: ", err));
  };

  // Download whitesheet functionality
  const handleDownloadWhitesheet = async () => {
    setIsDownloading(true);
    const hide = message.loading("Downloading PDF...");
    try {
      const { blob, filename } = await downloadWhitesheet(callContent?.call_request_id, 'pdf');
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename); // Use backend-provided filename
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      message.success("AI Call Summary downloaded in PDF");
    } finally {
      setIsDownloading(false);
      hide();
    }
  };

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    const hide = message.loading("Downloading Excel...");
    try {
      const { blob, filename } = await downloadWhitesheet(callContent?.call_request_id, 'excel');
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename); // Use backend-provided filename
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      message.success("AI Call Summary downloaded in Excel");
    } finally {
      setIsDownloading(false);
      hide();
    }
  };

  if (!hasData) {
    return (
      <div
        style={{
          padding: "60px 40px",
          textAlign: "center",
          backgroundColor: isDark ? "#04002b" : "#fafafa",
          borderRadius: "12px",
          border: `1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)"
          }`,
          boxShadow: isDark
            ? "0 4px 12px rgba(0, 0, 0, 0.3)"
            : "0 2px 8px rgba(0, 0, 0, 0.06)",
        }}
      >
        <div
          style={{
            padding: "20px",
            borderRadius: "50%",
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.02)",
            display: "inline-block",
            marginBottom: "20px",
          }}
        >
          <InfoCircleOutlined
            style={{
              fontSize: "48px",
              color: isDark ? "#666" : "#ccc",
            }}
          />
        </div>
        <Title
          level={4}
          style={{
            color: isDark ? "#ccc" : "#666",
            marginBottom: "8px",
          }}
        >
          No Call Summary Available
        </Title>
        <Text
          style={{
            color: isDark ? "#999" : "#999",
            fontSize: "14px",
          }}
        >
          Call summary data will appear here once the call is processed
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "95%",
        backgroundColor: currentTheme.callhistory.searchBackground,
        borderRadius: "16px",
        padding: "24px",
        height: "100%", // FIXED CONTAINER SIZE - Take full height of parent
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // FIXED CONTAINER SIZE - Prevent main container overflow
      }}
    >
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        style={{ marginBottom: "24px", flexShrink: 0 }}
      >
        <Flex align="center" gap={16}>
          <div
            style={{
              padding: "12px",
              borderRadius: "12px",
              // backgroundColor: `${currentTheme.loginPage.loginButtonColor}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
             <Tooltip title="Back to Call List">
            <IconButton
              aria-label="back"
              sx={{ color: "#ff4e3a", paddingBottom: 0 }}
              onClick={() => {
                setRenderType("callList");
                setSelectedMember("");
              }}
            >
              <KeyboardBackspaceIcon sx={{ fontSize: 35 }} />
            </IconButton>
          </Tooltip>
          </div>
          <div>
            <Title
              level={2}
              style={{
                margin: 0,
                color: isDark ? "#fff" : "#333",
                marginBottom: "4px",
              }}
            >
              Call Summary
            </Title>
            <Text
              style={{
                color: isDark ? "#ccc" : "#666",
                fontSize: "14px",
              }}
            >
              Comprehensive call analysis and breakdown
            </Text>
          </div>
         
        </Flex>

        <Flex align="center" gap={12}>
           {/* Verification Status */}
           {callContent?.verified ? (
            <ThemedChip
              variant="success"
              size="medium"
              customStyle={{
                borderRadius: "12px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <CheckCircleOutlined style={{ fontSize: "14px" }} />
              Verified
            </ThemedChip>
          ) : (
            <ThemedChip
              variant="warning"
              size="medium"
              customStyle={{
                borderRadius: "12px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <ExclamationCircleOutlined style={{ fontSize: "14px" }} />
              Not Verified
            </ThemedChip>
          )}
          <Tooltip
            title="Copied to clipboard!"
            open={copyTooltipOpen}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            onClose={() => setCopyTooltipOpen(false)}
          >
            <IconButton
              onClick={handleCopyToClipboard}
              style={{
                color: isDark ? "#fff" : "#333",
                padding: "10px",
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.04)",
                borderRadius: "8px",
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Download AI Call Summary in PDF">
            <IconButton
              onClick={handleDownloadWhitesheet}
              disabled={isDownloading}
              style={{
                color: isDark ? "#fff" : "#333",
                padding: "10px",
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.04)",
                borderRadius: "8px",
                opacity: isDownloading ? 0.6 : 1,
              }}
            >
              <img 
                src={`/icons/pdf-${isDark ? "dark" : "light"}-icon.png`}
                alt="PDF Download" 
                style={{ width: "18px", height: "18px" }}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Download AI Call Summary in Excel">
            <IconButton
              onClick={handleDownloadExcel}
              disabled={isDownloading}
              style={{
                color: isDark ? "#fff" : "#333",
                padding: "10px",
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.04)",
                borderRadius: "8px",
                opacity: isDownloading ? 0.6 : 1,
              }}
            >
              <img 
                src={`/icons/excel-${isDark ? "dark" : "light"}-icon.png`}
                alt="Excel Download" 
                style={{ width: "18px", height: "18px" }}
              />
            </IconButton>
          </Tooltip>          
          {/* <div
            onClick={() => setViewMore(!viewMore)}
            style={{
              padding: "8px 16px",
              backgroundColor: currentTheme.loginPage.loginButtonColor,
              color: "white",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
              userSelect: "none",
            }}
          >
            {!viewMore ? "Expand View" : "Collapse View"}
          </div> */}
        </Flex>
      </Flex>

      {/* Content Sections */}
      <div
        style={{
          flex: 1,
          paddingRight: "8px",
          overflow: "auto", // FIXED CONTAINER SIZE - Add scrolling for content sections
          minHeight: 0, // FIXED CONTAINER SIZE - Allow flex child to shrink
          // Custom scrollbar styling
          scrollbarWidth: 'thin',
          scrollbarColor: `${currentTheme.callhistory.borderColor} ${currentTheme.callhistory.searchBackground}`,
        }}
        className="call-summary-content-scroll"
      >
        <style jsx>{`
          .call-summary-content-scroll::-webkit-scrollbar {
            width: 8px;
            background: ${currentTheme.callhistory.searchBackground};
          }
          .call-summary-content-scroll::-webkit-scrollbar-track {
            background: ${currentTheme.callhistory.searchBackground};
            border-radius: 4px;
            border: 1px solid ${currentTheme.callhistory.borderColor}20;
          }
          .call-summary-content-scroll::-webkit-scrollbar-thumb {
            background-color: ${currentTheme.callhistory.borderColor};
            border-radius: 4px;
            border: none;
          }
          .call-summary-content-scroll::-webkit-scrollbar-thumb:hover {
            background-color: ${themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
          }
          .call-summary-content-scroll::-webkit-scrollbar-corner {
            background: ${currentTheme.callhistory.searchBackground};
          }
        `}</style>
        {/* Member Details */}
        {callSummaryData.member_details &&
          renderSection(
            "Member Information",
            callSummaryData.member_details,
            "memberDetails",
            <UserOutlined />,
            "Member identification and contact details"
          )}

        {/* Detailed Breakdown */}
        {callSummaryData.detailed_breakdown &&
          renderSection(
            "Detailed Breakdown",
            callSummaryData.detailed_breakdown,
            "detailedBreakdown",
            <PhoneOutlined />,
            "Comprehensive details and reference information"
          )}

        {/* Call Details */}
        {callSummaryData.call_details &&
          renderSection(
            "Call Details",
            callSummaryData.call_details,
            "callDetails",
            <PhoneOutlined />,
            "Status and outcome information for this call"
          )}
      </div>
    </div>
  );
};

export default CallSummaryDisplay;
